// Google Analytics Utility mit DSGVO-Konformität
// Tracking ID: G-MLXWMTB50N

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

const GA_TRACKING_ID = 'G-MLXWMTB50N';

/**
 * Initialisiert Google Analytics
 * Wird nur aufgerufen, wenn der Nutzer zugestimmt hat
 */
export const initGA = (): void => {
  // Prüfe ob bereits initialisiert
  if (window.gtag) {
    console.log('✅ Google Analytics bereits initialisiert');
    return;
  }

  // Google Tag Script laden
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);

  // DataLayer und gtag initialisieren
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer?.push(arguments);
  };

  window.gtag!('js', new Date());
  window.gtag!('config', GA_TRACKING_ID, {
    anonymize_ip: true, // IP-Anonymisierung (DSGVO-konform)
    cookie_flags: 'SameSite=None;Secure' // Cookie-Sicherheit
  });

  console.log('✅ Google Analytics initialisiert:', GA_TRACKING_ID);
};

/**
 * Deaktiviert Google Analytics
 * Wird aufgerufen, wenn Nutzer ablehnt oder Einwilligung widerruft
 */
export const disableGA = (): void => {
  // Google Analytics Opt-Out
  (window as any)[`ga-disable-${GA_TRACKING_ID}`] = true;

  // Lösche GA Cookies
  const gaCookies = ['_ga', '_gat', '_gid'];
  gaCookies.forEach(cookieName => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });

  console.log('🚫 Google Analytics deaktiviert');
};

/**
 * Prüft ob Google Analytics aktiv ist
 */
export const isGAActive = (): boolean => {
  return typeof window.gtag === 'function' &&
         !(window as any)[`ga-disable-${GA_TRACKING_ID}`];
};

/**
 * Sendet ein Pageview-Event
 * @param url - URL der Seite (optional, Standard: aktueller Pfad)
 */
export const trackPageView = (url?: string): void => {
  if (!isGAActive()) return;

  window.gtag!('event', 'page_view', {
    page_path: url || window.location.pathname + window.location.search,
    page_title: document.title,
    page_location: window.location.href
  });

  console.log('📊 Page view getrackt:', url || window.location.pathname);
};

/**
 * Sendet ein Custom Event
 * @param action - Event-Aktion (z.B. 'add_to_cart')
 * @param category - Event-Kategorie (z.B. 'ecommerce')
 * @param label - Event-Label (optional)
 * @param value - Event-Wert (optional)
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
): void => {
  if (!isGAActive()) return;

  window.gtag!('event', action, {
    event_category: category,
    event_label: label,
    value: value
  });

  console.log('📊 Event getrackt:', { action, category, label, value });
};

/**
 * E-Commerce Events
 */

// Produkt zu Warenkorb hinzugefügt
export const trackAddToCart = (item: {
  id: string;
  name: string;
  category?: string;
  price: number;
  quantity?: number;
}): void => {
  if (!isGAActive()) return;

  window.gtag!('event', 'add_to_cart', {
    currency: 'EUR',
    value: item.price,
    items: [{
      item_id: item.id,
      item_name: item.name,
      item_category: item.category || 'Reparatur',
      price: item.price,
      quantity: item.quantity || 1
    }]
  });

  console.log('🛒 Add to cart getrackt:', item.name);
};

// Checkout begonnen
export const trackBeginCheckout = (items: any[], value: number): void => {
  if (!isGAActive()) return;

  window.gtag!('event', 'begin_checkout', {
    currency: 'EUR',
    value: value,
    items: items
  });

  console.log('💳 Checkout begonnen:', value);
};

// Kauf abgeschlossen
export const trackPurchase = (transactionId: string, items: any[], value: number): void => {
  if (!isGAActive()) return;

  window.gtag!('event', 'purchase', {
    transaction_id: transactionId,
    currency: 'EUR',
    value: value,
    items: items
  });

  console.log('✅ Kauf getrackt:', transactionId, value);
};

// Produkt angesehen
export const trackViewItem = (item: {
  id: string;
  name: string;
  category?: string;
  price: number;
}): void => {
  if (!isGAActive()) return;

  window.gtag!('event', 'view_item', {
    currency: 'EUR',
    value: item.price,
    items: [{
      item_id: item.id,
      item_name: item.name,
      item_category: item.category || 'Reparatur',
      price: item.price
    }]
  });

  console.log('👁️ Item angesehen:', item.name);
};

/**
 * Tracking-Helper für verschiedene Aktionen
 */
export const GAEvents = {
  // Navigation
  navigation: (label: string) => trackEvent('click', 'navigation', label),

  // Kontakt
  contactFormSubmit: () => trackEvent('submit', 'contact', 'contact_form'),
  phoneClick: () => trackEvent('click', 'contact', 'phone_number'),
  emailClick: () => trackEvent('click', 'contact', 'email'),
  whatsappClick: () => trackEvent('click', 'contact', 'whatsapp'),

  // Warenkorb
  cartOpen: () => trackEvent('open', 'cart', 'cart_sidebar'),
  cartClose: () => trackEvent('close', 'cart', 'cart_sidebar'),

  // Reparatur
  repairView: (device: string, repair: string) =>
    trackEvent('view', 'repair', `${device} - ${repair}`),
  repairSelected: (device: string, repair: string) =>
    trackEvent('select', 'repair', `${device} - ${repair}`),

  // Blog
  blogPostView: (title: string) => trackEvent('view', 'blog', title),
  blogPostRead: (title: string) => trackEvent('read', 'blog', title),

  // Downloads
  downloadTracking: () => trackEvent('download', 'shipping', 'tracking_label'),

  // Social
  facebookClick: () => trackEvent('click', 'social', 'facebook'),
  instagramClick: () => trackEvent('click', 'social', 'instagram'),
  googleReviewClick: () => trackEvent('click', 'social', 'google_review'),

  // Error
  error404: () => trackEvent('error', 'navigation', '404_page'),
  paymentError: () => trackEvent('error', 'payment', 'payment_failed')
};
